/**
 * One event row of the notification setup grid.
 *
 * Renders the tenant's *effective* configuration — shipped defaults with this
 * tenant's override applied — plus whatever the admin has staged but not saved.
 * A channel the platform does not ship this event on renders as a locked,
 * unticked box: the API only accepts narrowing, never widening.
 *
 * Two things can kill the email columns, and the row says which:
 *  - the tenant email kill switch (every row at once), or
 *  - this row's frequency being `Off`, which means bell only.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { Switch } from '../../../ui/switch';
import { TableCell, TableRow } from '../../../ui/table';
import { Lock, RotateCcw } from 'lucide-react';
import { NotificationFrequencySelect } from './NotificationFrequencySelect';
import {
  CHANNEL_LABELS,
  SETUP_CHANNELS,
  deliversNoEmail,
  describeDelivery,
  isEmailSilenced,
  resolveFrequency,
  type EventConfig,
  type SetupChoice,
  type SetupEvent,
} from './types';

interface NotificationEventRowProps {
  event: SetupEvent;
  config: EventConfig;
  dirty: boolean;
  canConfigure: boolean;
  frequencyChoices: SetupChoice[];
  /**
   * Tenant-wide cadence this row falls back to when it has none of its own.
   * `''` means the tenant set none, so the row falls back to its own platform
   * default instead.
   */
  defaultFrequency: NotificationTenantDefaultFrequency;
  /** Tenant email master switch — a dead email column is explained, not hidden. */
  emailsEnabled: boolean;
  onUpdate: (event: SetupEvent, patch: Partial<EventConfig>) => void;
  onToggleChannel: (event: SetupEvent, channel: NotificationChannel, on: boolean) => void;
  onReset: (event: SetupEvent) => void;
}

export function NotificationEventRow({
  event,
  config,
  dirty,
  canConfigure,
  frequencyChoices,
  defaultFrequency,
  emailsEnabled,
  onUpdate,
  onToggleChannel,
  onReset,
}: NotificationEventRowProps) {
  const locked = !canConfigure;
  // What this row inherits when it is not pinned: the tenant default, or its
  // own platform default when the tenant has set no blanket cadence.
  const inheritedFrequency = resolveFrequency(null, defaultFrequency, event.platformFrequency);
  // `Off` is a per-row kill switch for email; the master switch is the same
  // thing for the whole tenant. Either one silences the mail channels.
  const silencedByFrequency = isEmailSilenced(config.frequency ?? inheritedFrequency);
  const mailChannelDead = !emailsEnabled || silencedByFrequency;
  // An event that ships no mail channel cannot be emailed or digested at any
  // cadence — the bell is granted by every frequency, so all five values do
  // exactly the same thing. Leaving the selector live invites an admin to
  // "turn this one down" and change nothing. Show what it runs on, read-only.
  const canMail = SETUP_CHANNELS.some(
    (channel) => channel !== 'bell' && event.availableChannels.includes(channel),
  );
  // What the row will actually do, stated in words. The cadence on its own
  // cannot say it: Daily means "its own email" or "a count in the summary"
  // depending on which mail box is ticked, and the two used to be the same
  // thing. Only channels the platform ships count — a tick the API would
  // refuse must not appear in the promise.
  const effectiveChannels = config.channels.filter((channel) =>
    event.availableChannels.includes(channel),
  );
  const effectiveFrequency = config.frequency ?? inheritedFrequency;

  return (
    <TableRow className={dirty ? 'bg-primary/5' : undefined}>
      <TableCell className="align-top">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{event.name}</span>
          {!event.canDisable && (
            <Badge variant="outline" className="gap-1">
              <Lock className="h-3 w-3" />
              Required
            </Badge>
          )}
          {dirty && <Badge variant="secondary">Unsaved</Badge>}
          {!dirty && event.isOverridden && <Badge variant="outline">Customised</Badge>}
          {!event.isActive && <Badge variant="outline">Retired</Badge>}
          {!event.moduleEnabled && <Badge variant="outline">Module off</Badge>}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {event.subdomainLabel}
          {event.description ? ` — ${event.description}` : ''}
        </p>
      </TableCell>

      <TableCell className="text-center align-top">
        <Switch
          checked={config.enabled}
          disabled={locked || !event.canDisable}
          aria-label={`${event.name} enabled`}
          onCheckedChange={(checked) => onUpdate(event, { enabled: checked })}
        />
      </TableCell>

      {SETUP_CHANNELS.map((channel) => {
        const available = event.availableChannels.includes(channel);
        const suppressed = mailChannelDead && channel !== 'bell';
        return (
          <TableCell key={channel} className="text-center align-top">
            <Checkbox
              checked={available && config.channels.includes(channel)}
              // A frequency of Off never emails, so the mail boxes stop being
              // an editable choice rather than lying about what will happen.
              // The tenant kill switch stays merely dimmed — it is temporary,
              // and the per-event choice underneath it still matters.
              disabled={
                locked
                || !available
                || !config.enabled
                || (silencedByFrequency && channel !== 'bell')
              }
              aria-label={`${event.name} ${CHANNEL_LABELS[channel]}`}
              className={suppressed ? 'opacity-50' : undefined}
              onCheckedChange={(checked) => onToggleChannel(event, channel, checked === true)}
            />
            {!available && <span className="sr-only">Not available for this event</span>}
            {available && suppressed && (
              <span className="sr-only">
                {silencedByFrequency
                  ? 'Frequency is Off — this event is never emailed'
                  : 'Notification email is switched off for this tenant'}
              </span>
            )}
          </TableCell>
        );
      })}

      <TableCell className="align-top">
        {canMail ? (
          <NotificationFrequencySelect
            value={config.frequency}
            inheritedFrequency={inheritedFrequency}
            choices={frequencyChoices}
            disabled={locked}
            label={`${event.name} frequency`}
            deliveryHint={describeDelivery(effectiveChannels, effectiveFrequency)}
            deliverySilent={deliversNoEmail(effectiveChannels, effectiveFrequency)}
            onChange={(frequency) => onUpdate(event, { frequency })}
          />
        ) : (
          <div className="text-sm text-muted-foreground">
            <span>Bell only</span>
            <p className="mt-0.5 text-xs">
              This event has no email or digest, so cadence does not apply.
            </p>
          </div>
        )}
      </TableCell>

      <TableCell className="text-right align-top">
        <Button
          variant="ghost"
          size="sm"
          disabled={locked || (!event.isOverridden && !dirty)}
          title="Reset to the platform default"
          aria-label={`Reset ${event.name} to the platform default`}
          onClick={() => onReset(event)}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
